import React, { useState, useEffect } from "react";
import {
    TextField, Button, Grid, FormHelperText, FormControl, FormLabel,
    Typography, Paper, MenuItem, Select, InputLabel,
    Radio, RadioGroup, FormControlLabel, Tooltip, Box, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Person as PersonIcon, Edit as EditIcon } from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import {
    SaveTemperatureRecord,
    UpdateTemperatureRecord,
    GetTemperatureRecordById,
} from "../../../services/TemperatureTrackerService";
import { GetEquipments } from "../../../services/GeneralMaintenanceService";
import CommonLoader from "../../common/CommonLoader";
import UserSelectorDialog from "../../common/UserSelectorDialog";
import useDropdownList from "../../common/useDropdownList";
import { DROPDOWN_LIST_TYPES } from "../../../services/DropdownListService";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const TEMP_LIMIT = 999.9;

const TEMP_FIELDS = [
    { key: "currentTemperatureTop", label: "Current Temperature Top" },
    { key: "currentTemperatureMiddle", label: "Current Temperature Middle" },
    { key: "currentTemperatureBottom", label: "Current Temperature Bottom" },
    { key: "minTemperature", label: "Min Temperature" },
    { key: "maxTemperature", label: "Max Temperature" },
];

const toLocalDateTimeInput = (value) => {
    const d = value ? new Date(value) : new Date();
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};


const LabelWithTooltip = ({ label, tooltip }) => (
    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {label}
        <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon style={{ fontSize: 16, color: "#1976d2", cursor: "pointer" }} />
        </Tooltip>
    </span>
);

const YesNoField = ({ label, tooltip, value, onChange }) => (
    <FormControl component="fieldset" size="small">
        <FormLabel
            component="legend"
            sx={{ fontSize: "0.8rem", "&.Mui-focused": { color: "text.secondary" } }}
        >
            {tooltip ? <LabelWithTooltip label={label} tooltip={tooltip} /> : label}
        </FormLabel>
        <RadioGroup
            row
            value={value ? "yes" : "no"}
            onChange={(e) => onChange(e.target.value === "yes")}
        >
            <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
            <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
        </RadioGroup>
    </FormControl>
);

const AddTemperatureTracker = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const scanState = location.state || {};

    const [dateCollected, setDateCollected] = useState(toLocalDateTimeInput());
    const [equipment, setEquipment] = useState(scanState.equipment || "");
    const [equipmentType, setEquipmentType] = useState(scanState.equipmentType || "");
    const [readingNumber, setReadingNumber] = useState(scanState.readingNumber || "");
    const [currentTemperatureTop, setCurrentTemperatureTop] = useState("");
    const [currentTemperatureMiddle, setCurrentTemperatureMiddle] = useState("");
    const [currentTemperatureBottom, setCurrentTemperatureBottom] = useState("");
    const [minTemperature, setMinTemperature] = useState("");
    const [maxTemperature, setMaxTemperature] = useState("");
    const [tempsDirty, setTempsDirty] = useState(false);
    const [batteryLow, setBatteryLow] = useState(false);
    const [freezeBlank, setFreezeBlank] = useState(false);
    const [samplesPresent, setSamplesPresent] = useState(false);
    const [isWithinRange, setIsWithinRange] = useState(false);
    const [thermometerReset, setThermometerReset] = useState(false);
    const [comments, setComments] = useState("");
    const [actionTaken, setActionTaken] = useState("");
    const [qcNotes, setQcNotes] = useState("");
    const [initials, setInitials] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [userSelectorOpen, setUserSelectorOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [outOfRangeOpen, setOutOfRangeOpen] = useState(false);
    const [outOfRangeItems, setOutOfRangeItems] = useState([]);

    const [equipLowTemp, setEquipLowTemp] = useState(scanState.lowTemp ?? null);
    const [equipHighTemp, setEquipHighTemp] = useState(scanState.highTemp ?? null);
    const [thermometerCount, setThermometerCount] = useState(scanState.thermometerCount ?? 3);
    const [allEquipment, setAllEquipment] = useState([]);

    const fromScanner = !!scanState.fromScanner;

    const showTop = thermometerCount >= 1;
    const showMiddle = thermometerCount >= 2;
    const showBottom = thermometerCount >= 3;

    const isSecondReading = (readingNumber || "").trim().toLowerCase() === "second";

    useEffect(() => {
        GetEquipments()
            .then((res) => setAllEquipment(res?.results || []))
            .catch(() => {});
    }, []);

    const KNOWN_TYPES = ["Incubator", "Refrigerator", "Freezer", "Oven"];

    const deriveEquipmentType = (item) => {
        const lower = (item || "").toLowerCase();
        if (lower.includes("incubator")) return "Incubator";
        if (lower.includes("refrigerator") || lower.includes("fridge")) return "Refrigerator";
        if (lower.includes("freezer")) return "Freezer";
        if (lower.includes("oven")) return "Oven";
        return "";
    };

    useEffect(() => {
        if (!equipment || allEquipment.length === 0) return;
        const matched = allEquipment.find(
            (e) => (e.description || "").trim().toLowerCase() === equipment.trim().toLowerCase()
        );
        if (matched) {
            setEquipLowTemp(matched.lowTemp ?? null);
            setEquipHighTemp(matched.highTemp ?? null);
            const count = [matched.thermometer1, matched.thermometer2, matched.thermometer3]
                .filter((t) => t && t.trim()).length;
            setThermometerCount(count > 0 ? count : 3);
            setOutOfRangeItems([]);

            setEquipmentType((prev) => {
                const trimmed = prev.trim();
                if (!trimmed || !KNOWN_TYPES.includes(trimmed)) {
                    return deriveEquipmentType(matched.item) || trimmed;
                }
                return trimmed;
            });
        }
    }, [equipment, allEquipment]);

    useEffect(() => {
        if (id) fetchRecordData(parseInt(id, 10));
    }, [id]);

    const fetchRecordData = async (recordId) => {
        setLoading(true);
        try {
            const response = await GetTemperatureRecordById(recordId);
            if (response && response.results) {
                const r = response.results;
                setDateCollected(toLocalDateTimeInput(r.dateCollected));
                setEquipment((r.equipment || "").trim());
                setEquipmentType((r.equipmentType || "").trim());
                setReadingNumber((r.readingNumber || "").trim());
                setCurrentTemperatureTop(r.currentTemperatureTop ?? "");
                setCurrentTemperatureMiddle(r.currentTemperatureMiddle ?? "");
                setCurrentTemperatureBottom(r.currentTemperatureBottom ?? "");
                setMinTemperature(r.minTemperature ?? "");
                setMaxTemperature(r.maxTemperature ?? "");
                setBatteryLow(r.batteryLow || false);
                setFreezeBlank(r.freezeBlank || false);
                setSamplesPresent(r.samplesPresent || false);
                setIsWithinRange(r.isWithinRange || false);
                setThermometerReset(r.thermometerReset || false);
                setComments(r.comments || "");
                setActionTaken(r.actionTaken || "");
                setQcNotes(r.qcNotes || "");
                setInitials(r.initials || "");
            } else {
                NotificationManager.error("Failed to fetch record data");
            }
        } catch (error) {
            NotificationManager.error("Error fetching record data");
        } finally {
            setLoading(false);
        }
    };

    const getTemperatureErrors = () => {
        const values = {
            currentTemperatureTop,
            currentTemperatureMiddle,
            currentTemperatureBottom,
            minTemperature,
            maxTemperature,
        };

        const tempErrors = {};

        TEMP_FIELDS.forEach(({ key, label }) => {
            const raw = values[key];
            if (raw === "" || raw === null || raw === undefined) return;
            const parsed = parseFloat(raw);
            if (isNaN(parsed)) {
                tempErrors[key] = `${label} must be a number`;
            } else if (Math.abs(parsed) > TEMP_LIMIT) {
                tempErrors[key] = `${label} must be between -${TEMP_LIMIT} and ${TEMP_LIMIT} °C`;
            }
        });

        const min = parseFloat(minTemperature);
        const max = parseFloat(maxTemperature);
        if (!isNaN(min) && !isNaN(max) && min > max &&
            !tempErrors.minTemperature && !tempErrors.maxTemperature) {
            tempErrors.minTemperature = "Min Temperature cannot be greater than Max Temperature";
        }

        return tempErrors;
    };

    useEffect(() => {
        if (!tempsDirty) return;

        const timer = setTimeout(() => {
            const tempErrors = getTemperatureErrors();

            setErrors((prev) => {
                const next = { ...prev };
                TEMP_FIELDS.forEach(({ key }) => delete next[key]);
                return { ...next, ...tempErrors };
            });
        }, 700);

        return () => clearTimeout(timer);
    }, [
        tempsDirty,
        currentTemperatureTop,
        currentTemperatureMiddle,
        currentTemperatureBottom,
        minTemperature,
        maxTemperature,
    ]);

    const handleTempChange = (setter) => (e) => {
        setTempsDirty(true);
        setter(e.target.value);
    };

    const handleTempBlur = () => checkTempRange();

    const handleAcknowledge = () => setOutOfRangeOpen(false);

    const enteredTemps = () => [
        currentTemperatureTop,
        currentTemperatureMiddle,
        currentTemperatureBottom,
    ].filter((v) => v !== "" && v !== null && v !== undefined);

    const computeOutOfRange = () => {
        if (equipLowTemp === null || equipHighTemp === null) return [];
        return [
            { label: "Top", value: currentTemperatureTop },
            { label: "Middle", value: currentTemperatureMiddle },
            { label: "Bottom", value: currentTemperatureBottom },
        ].filter((c) => c.value !== "" && c.value !== null && c.value !== undefined)
         .filter((c) => {
             const v = parseFloat(c.value);
             return v < parseFloat(equipLowTemp) || v > parseFloat(equipHighTemp);
         });
    };

    const checkTempRange = () => {
        if (equipLowTemp === null || equipHighTemp === null) return;
        if (enteredTemps().length === 0) return;

        const outOfRange = computeOutOfRange();
        setIsWithinRange(outOfRange.length === 0);

        if (outOfRange.length > 0) {
            setOutOfRangeItems(outOfRange);
            setOutOfRangeOpen(true);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!dateCollected) newErrors.dateCollected = "Date Collected is required";
        if (!equipment) newErrors.equipment = "Equipment is required";
        if (!equipmentType) newErrors.equipmentType = "Equipment Type is required";
        if (!readingNumber) newErrors.readingNumber = "Reading Number is required";
        if (!initials) newErrors.initials = "Initials are required";

        const tempErrors = getTemperatureErrors();
        Object.assign(newErrors, tempErrors);

        const tempsUsable = Object.keys(tempErrors).length === 0;
        const outOfRange = tempsUsable ? computeOutOfRange() : [];
        const limitsKnown = equipLowTemp !== null && equipHighTemp !== null;

        const rangeDerivable = limitsKnown && tempsUsable && enteredTemps().length > 0;
        const withinRange = rangeDerivable ? outOfRange.length === 0 : isWithinRange;
        setIsWithinRange(withinRange);

        if (outOfRange.length > 0) {
            if (!comments) newErrors.comments = "Comment is required when temperature is out of range";
            if (!actionTaken) newErrors.actionTaken = "Action Taken is required when temperature is out of range";
            if (!comments || !actionTaken) {
                setOutOfRangeItems(outOfRange);
                setOutOfRangeOpen(true);
            }
        }

        setErrors(newErrors);
        return { ok: Object.keys(newErrors).length === 0, withinRange };
    };

    const handleSave = async () => {
        const { ok, withinRange } = validate();
        if (!ok) return;

        const recordData = {
            id: id ? parseInt(id, 10) : undefined,
            dateCollected,
            equipment: equipment.trim(),
            equipmentType: equipmentType.trim(),
            readingNumber: readingNumber.trim(),
            currentTemperatureTop: currentTemperatureTop !== "" ? parseFloat(currentTemperatureTop) : null,
            currentTemperatureMiddle: currentTemperatureMiddle !== "" ? parseFloat(currentTemperatureMiddle) : null,
            currentTemperatureBottom: currentTemperatureBottom !== "" ? parseFloat(currentTemperatureBottom) : null,
            minTemperature: minTemperature !== "" ? parseFloat(minTemperature) : null,
            maxTemperature: maxTemperature !== "" ? parseFloat(maxTemperature) : null,
            batteryLow,
            freezeBlank,
            samplesPresent,
            isWithinRange: withinRange,
            thermometerReset,
            comments,
            actionTaken,
            qcNotes,
            initials,
        };

        setLoading(true);
        try {
            if (id) {
                const response = await UpdateTemperatureRecord(recordData);
                if (response.statusCode === 200) {
                    NotificationManager.success("Record updated successfully");
                    navigate("/quality-control/temperature-tracker");
                } else {
                    throw new Error("Failed to update record");
                }
            } else {
                const response = await SaveTemperatureRecord(recordData);
                if (response.statusCode === 200) {
                    NotificationManager.success("Record saved successfully");
                    navigate("/quality-control/temperature-tracker");
                } else {
                    throw new Error("Failed to save record");
                }
            }
        } catch (error) {
            NotificationManager.error(error.message || "Error saving record");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => navigate("/quality-control/temperature-tracker");

    // Admin-managed lists (Maintenance > Dropdown Lists); a saved value no longer in the list is kept.
    const commentOptions = useDropdownList(DROPDOWN_LIST_TYPES.TEMPERATURE_COMMENT, comments);
    const actionTakenOptions = useDropdownList(DROPDOWN_LIST_TYPES.TEMPERATURE_ACTION_TAKEN, actionTaken);

    const tempLimitNote = equipLowTemp !== null && equipHighTemp !== null
        ? ` (allowed: ${equipLowTemp}°C – ${equipHighTemp}°C)`
        : "";

    return (
        <>
            <Paper elevation={3} sx={{ padding: 3, margin: "auto" }}>
                <Typography variant="h5" gutterBottom>
                    {id ? "Edit Temperature Record" : "Add Temperature Record"}
                </Typography>

                <Grid container spacing={2}>

                    {/* Date Collected */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Date Collected" type="datetime-local"
                            value={dateCollected}
                            onChange={(e) => setDateCollected(e.target.value)}
                            fullWidth InputLabelProps={{ shrink: true }} required size="small"
                            error={!!errors.dateCollected} helperText={errors.dateCollected}
                        />
                    </Grid>

                    {/* Equipment Type */}
                    <Grid item xs={12} sm={4}>
                        {fromScanner && !id ? (
                            <TextField
                                label="Equipment Type" value={equipmentType}
                                fullWidth size="small" InputProps={{ readOnly: true }}
                                sx={{ "& .MuiInputBase-root": { backgroundColor: "grey.100" } }}
                            />
                        ) : (
                            <FormControl fullWidth size="small" error={!!errors.equipmentType}>
                                <InputLabel>Equipment Type *</InputLabel>
                                <Select
                                    value={equipmentType}
                                    onChange={(e) => setEquipmentType(e.target.value)}
                                    label="Equipment Type *"
                                >
                                    <MenuItem value="">Select Type</MenuItem>
                                    <MenuItem value="Incubator">Incubator</MenuItem>
                                    <MenuItem value="Refrigerator">Refrigerator</MenuItem>
                                    <MenuItem value="Freezer">Freezer</MenuItem>
                                    <MenuItem value="Oven">Oven</MenuItem>
                                </Select>
                                {errors.equipmentType && <FormHelperText>{errors.equipmentType}</FormHelperText>}
                            </FormControl>
                        )}
                    </Grid>

                    {/* Equipment */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Equipment *"
                            value={equipment}
                            onChange={(e) => setEquipment(e.target.value)}
                            fullWidth size="small"
                            error={!!errors.equipment} helperText={errors.equipment}
                            InputProps={fromScanner && !id ? { readOnly: true } : {}}
                            sx={fromScanner && !id ? { "& .MuiInputBase-root": { backgroundColor: "grey.100" } } : {}}
                        />
                    </Grid>

                    {/* Reading Number */}
                    <Grid item xs={12} sm={4}>
                        {fromScanner && !id ? (
                            <TextField
                                label="Reading Number" value={readingNumber}
                                fullWidth size="small" InputProps={{ readOnly: true }}
                                sx={{ "& .MuiInputBase-root": { backgroundColor: "grey.100" } }}
                            />
                        ) : (
                            <FormControl fullWidth size="small" error={!!errors.readingNumber}>
                                <InputLabel>Reading Number *</InputLabel>
                                <Select
                                    value={readingNumber}
                                    onChange={(e) => setReadingNumber(e.target.value)}
                                    label="Reading Number *"
                                >
                                    <MenuItem value="">Select Reading</MenuItem>
                                    <MenuItem value="First">First</MenuItem>
                                    <MenuItem value="Second">Second</MenuItem>
                                </Select>
                                {errors.readingNumber && <FormHelperText>{errors.readingNumber}</FormHelperText>}
                            </FormControl>
                        )}
                    </Grid>

                    {/* Current Temp Top */}
                    {showTop && (
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label={`Current Temperature Top (°C)${tempLimitNote}`}
                                type="number"
                                value={currentTemperatureTop}
                                onChange={handleTempChange(setCurrentTemperatureTop)}
                                onBlur={handleTempBlur}
                                fullWidth size="small"
                                inputProps={{ step: "0.1" }}
                                error={!!errors.currentTemperatureTop}
                                helperText={errors.currentTemperatureTop}
                            />
                        </Grid>
                    )}

                    {/* Current Temp Middle */}
                    {showMiddle && (
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label={`Current Temperature Middle (°C)${tempLimitNote}`}
                                type="number"
                                value={currentTemperatureMiddle}
                                onChange={handleTempChange(setCurrentTemperatureMiddle)}
                                onBlur={handleTempBlur}
                                fullWidth size="small"
                                inputProps={{ step: "0.1" }}
                                error={!!errors.currentTemperatureMiddle}
                                helperText={errors.currentTemperatureMiddle}
                            />
                        </Grid>
                    )}

                    {/* Current Temp Bottom */}
                    {showBottom && (
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label={`Current Temperature Bottom (°C)${tempLimitNote}`}
                                type="number"
                                value={currentTemperatureBottom}
                                onChange={handleTempChange(setCurrentTemperatureBottom)}
                                onBlur={handleTempBlur}
                                fullWidth size="small"
                                inputProps={{ step: "0.1" }}
                                error={!!errors.currentTemperatureBottom}
                                helperText={errors.currentTemperatureBottom}
                            />
                        </Grid>
                    )}

                    {/* Min Temp — first reading only */}
                    <Grid item xs={12} sm={4}>
                        <Tooltip
                            arrow
                            placement="top"
                            title={isSecondReading
                                ? "Min/Max are only recorded on the First reading of the day."
                                : "Minimum temperature as read from the thermometer."}
                        >
                            <TextField
                                label="Min Temperature (°C)" type="number"
                                value={minTemperature}
                                onChange={handleTempChange(setMinTemperature)}
                                disabled={isSecondReading}
                                fullWidth size="small" inputProps={{ step: "0.1" }}
                                error={!!errors.minTemperature}
                                helperText={errors.minTemperature}
                            />
                        </Tooltip>
                    </Grid>

                    {/* Max Temp — first reading only */}
                    <Grid item xs={12} sm={4}>
                        <Tooltip
                            arrow
                            placement="top"
                            title={isSecondReading
                                ? "Min/Max are only recorded on the First reading of the day."
                                : "Maximum temperature as read from the thermometer."}
                        >
                            <TextField
                                label="Max Temperature (°C)" type="number"
                                value={maxTemperature}
                                onChange={handleTempChange(setMaxTemperature)}
                                disabled={isSecondReading}
                                fullWidth size="small" inputProps={{ step: "0.1" }}
                                error={!!errors.maxTemperature}
                                helperText={errors.maxTemperature}
                            />
                        </Tooltip>
                    </Grid>

                    {/* Initials */}
                    <Grid item xs={12} sm={4}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {selectedUser || initials ? (
                                <Box sx={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    p: 1.5, border: 1,
                                    borderColor: errors.initials ? "error.main" : "grey.300",
                                    borderRadius: 1, backgroundColor: "grey.50", minHeight: "40px",
                                }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body2" fontWeight="medium">
                                            {selectedUser?.operator || initials}
                                        </Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => setUserSelectorOpen(true)} sx={{ color: "primary.main", ml: 1 }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<PersonIcon />}
                                    onClick={() => setUserSelectorOpen(true)}
                                    sx={{
                                        justifyContent: "flex-start", textTransform: "none", height: "40px",
                                        borderColor: errors.initials ? "error.main" : "grey.300",
                                        color: errors.initials ? "error.main" : "inherit",
                                    }}
                                >
                                    Select Initials
                                </Button>
                            )}
                            {errors.initials && <FormHelperText error>{errors.initials}</FormHelperText>}
                        </Box>
                    </Grid>

                    {/* Battery Low */}
                    <Grid item xs={12} sm={4}>
                        <YesNoField
                            label="Is Battery Low?"
                            tooltip="Check if any thermometer battery is low. If low, the battery must be changed — indicate in Comments that it has been changed."
                            value={batteryLow}
                            onChange={setBatteryLow}
                        />
                    </Grid>

                    {/* Freeze Blank */}
                    <Grid item xs={12} sm={4}>
                        <YesNoField
                            label="Is Freeze Blank Frozen?"
                            value={freezeBlank}
                            onChange={setFreezeBlank}
                        />
                    </Grid>

                    {/* Samples Present */}
                    <Grid item xs={12} sm={4}>
                        <YesNoField
                            label="Samples Present?"
                            tooltip="Indicate whether there are samples currently present in this equipment."
                            value={samplesPresent}
                            onChange={setSamplesPresent}
                        />
                    </Grid>

                    {/* Is Within Range */}
                    <Grid item xs={12} sm={4}>
                        <YesNoField
                            label="Is Temp Within Range?"
                            tooltip="Is the current temperature within the acceptable range for this equipment?"
                            value={isWithinRange}
                            onChange={setIsWithinRange}
                        />
                    </Grid>

                    {/* Thermometer Reset */}
                    <Grid item xs={12} sm={4}>
                        <YesNoField
                            label="Did You Reset The Thermometer?"
                            tooltip="On Saturday (or Holiday), if no other recordings are required, were the thermometers reset?"
                            value={thermometerReset}
                            onChange={setThermometerReset}
                        />
                    </Grid>

                    {/* Comments */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" error={!!errors.comments}>
                            <InputLabel>Comments</InputLabel>
                            <Select
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                label="Comments"
                                MenuProps={{ PaperProps: { sx: { maxWidth: 600 } } }}
                                sx={{ "& .MuiSelect-select": { whiteSpace: "normal" } }}
                            >
                                <MenuItem value="">Select Comment</MenuItem>
                                {commentOptions.map((option) => (
                                    <MenuItem key={option} value={option} sx={{ whiteSpace: "normal" }}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.comments && <FormHelperText>{errors.comments}</FormHelperText>}
                        </FormControl>
                    </Grid>

                    {/* Action Taken */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" error={!!errors.actionTaken}>
                            <InputLabel>Action Taken</InputLabel>
                            <Select
                                value={actionTaken}
                                onChange={(e) => setActionTaken(e.target.value)}
                                label="Action Taken"
                                MenuProps={{ PaperProps: { sx: { maxWidth: 600 } } }}
                                sx={{ "& .MuiSelect-select": { whiteSpace: "normal" } }}
                            >
                                <MenuItem value="">Select Action Taken</MenuItem>
                                {actionTakenOptions.map((option) => (
                                    <MenuItem key={option} value={option} sx={{ whiteSpace: "normal" }}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.actionTaken && <FormHelperText>{errors.actionTaken}</FormHelperText>}
                        </FormControl>
                    </Grid>

                    {/* QC Notes */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="QC Notes"
                            value={qcNotes}
                            onChange={(e) => setQcNotes(e.target.value)}
                            fullWidth multiline rows={2} size="small"
                        />
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
                        <Grid item>
                            <Button onClick={handleCancel} color="secondary" variant="outlined">Cancel</Button>
                        </Grid>
                        <Grid item>
                            <Button onClick={handleSave} color="primary" variant="contained">
                                Submit
                            </Button>
                        </Grid>
                    </Grid>

                </Grid>
            </Paper>

            {/* Out-of-range acknowledgment dialog */}
            <Dialog open={outOfRangeOpen} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "warning.dark" }}>
                    <WarningAmberIcon color="warning" />
                    Temperature Out of Range!
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        The following readings are outside the allowed range ({equipLowTemp}°C – {equipHighTemp}°C):
                    </Typography>
                    <List dense>
                        {outOfRangeItems.map((item) => (
                            <ListItem key={item.label} disableGutters>
                                <ListItemText
                                    primary={`${item.label}: ${item.value}°C`}
                                    sx={{ color: "error.main" }}
                                />
                            </ListItem>
                        ))}
                    </List>
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        Click Acknowledge to confirm. Comment and Action Taken must be noted.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="warning" onClick={handleAcknowledge}>
                        Acknowledge
                    </Button>
                </DialogActions>
            </Dialog>

            <UserSelectorDialog
                open={userSelectorOpen}
                onClose={() => setUserSelectorOpen(false)}
                onSelect={(user) => {
                    if (user) {
                        setSelectedUser(user);
                        setInitials(user.operator ? user.operator.trim() : "");
                    }
                }}
                multiSelect={false}
                filter="analyst"
            />

            <CommonLoader loading={loading} />
        </>
    );
};

export default AddTemperatureTracker;
